import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface ThemeSettings {
  [key: string]: any;
}

const ThemeContext = createContext<ThemeSettings | null>(null);

export function useTheme() {
  return useContext(ThemeContext);
}

function contrastColor(hex: string): string {
  if (!hex || !hex.startsWith("#") || hex.length < 7) return "#000000";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? "#000000" : "#ffffff";
}

function hexToHsl(hex: string): string | null {
  if (!hex || !hex.startsWith("#")) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const GOOGLE_FONTS_LOADED = new Set<string>();
// IMPORTANT: every font listed in `index.css` `@font-face` blocks (or
// aliased there) MUST appear here too — otherwise `loadGoogleFont` will
// race a redundant Google Fonts <link> against the local files. On
// luxurygive.com this exact bug cost +2.1s of LCP because the tenant's
// theme.fontFamily is set to "Cairo" (which is locally aliased to
// Tajawal in `index.css` line 327-329), but `Cairo` was missing from
// SELF_HOSTED_FONTS so the runtime would also fetch
//   https://fonts.googleapis.com/css2?family=Cairo:wght@...
// and block the critical render path on a 2-second cross-origin
// stylesheet that contributed nothing to the actual rendering.
const SELF_HOSTED_FONTS = new Set(["Inter", "Noto Sans Arabic", "Tajawal", "Cairo", "Kufam"]);
const SYSTEM_FONTS = new Set(["system-ui", "sans-serif", "serif", "monospace"]);

let GOOGLE_FONTS_PRECONNECTED = false;
function ensureGoogleFontsPreconnect() {
  if (GOOGLE_FONTS_PRECONNECTED) return;
  GOOGLE_FONTS_PRECONNECTED = true;
  // Warming both origins shaves ~100-300ms off the first Google-hosted font
  // because the TLS handshake happens in parallel with HTML parsing instead
  // of serially before the stylesheet (and then the woff2) can download.
  // gstatic must be crossorigin (fonts are always CORS); googleapis is the
  // CSS endpoint and doesn't need crossorigin.
  const head = document.head;
  const a = document.createElement("link");
  a.rel = "preconnect"; a.href = "https://fonts.googleapis.com";
  head.appendChild(a);
  const b = document.createElement("link");
  b.rel = "preconnect"; b.href = "https://fonts.gstatic.com"; b.crossOrigin = "anonymous";
  head.appendChild(b);
}

function loadGoogleFont(fontName: string) {
  if (!fontName || GOOGLE_FONTS_LOADED.has(fontName)) return;
  if (SYSTEM_FONTS.has(fontName) || SELF_HOSTED_FONTS.has(fontName)) return;
  GOOGLE_FONTS_LOADED.add(fontName);
  ensureGoogleFontsPreconnect();
  const link = document.createElement("link");
  link.rel = "stylesheet";
  // Only request the two weights that actually render above the fold (400
  // body, 700 headings). The previous "300;400;500;600;700;800" requested
  // ~6x more woff2 bytes; on average each weight is ~25KB so the saving is
  // ~100KB per font on slow mobile connections, with no visible difference
  // because intermediate weights round to the nearest defined weight.
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;700&display=swap`;
  document.head.appendChild(link);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeSettings | null>(null);

  useEffect(() => {
    const BASE_URL = import.meta.env.BASE_URL || "/";
    fetch(`${BASE_URL}api/theme/settings`, { credentials: "include" })
      .then(r => r.json())
      .then((data: ThemeSettings) => {
        setTheme(data);
        applyTheme(data);
        // Propagate tenant WebP settings to the img-proxy URL builder so all
        // subsequent image loads carry the right q / nowebp param.
        import("./imgSrc").then(({ setWebpConfig }) => {
          setWebpConfig({
            enabled: (data as any).webpConversionEnabled !== "false",
            quality: Math.max(50, Math.min(100, Number((data as any).webpQuality) || 85)),
          });
        }).catch(() => {});
      })
      .catch(() => {});
  }, []);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
}

// Register merchant-uploaded custom fonts as @font-face rules so the
// rest of applyTheme() can simply set `font-family: "DG Faseh"` and have
// it work. We dedupe by family name and inject a single <style> tag per
// font so re-applying the theme (template change, color tweak) doesn't
// pile up duplicate rules.
const REGISTERED_CUSTOM_FONTS = new Set<string>();
function registerCustomFonts(fonts: any[]) {
  if (!Array.isArray(fonts)) return;
  for (const f of fonts) {
    if (!f?.family || !Array.isArray(f.files) || f.files.length === 0) continue;
    // Key by family + the first file URL so re-uploading the same family
    // with a new file (e.g. swapping the regular weight) actually
    // refreshes the @font-face rule instead of being silently no-op'd.
    const key = `${f.family}|${f.files[0]?.url || ""}`;
    if (REGISTERED_CUSTOM_FONTS.has(key)) continue;
    REGISTERED_CUSTOM_FONTS.add(key);
    // Drop any prior <style data-custom-font="..."> for this same family
    // so we don't pile up stale @font-face rules on hot-reload.
    document.querySelectorAll(`style[data-custom-font="${CSS.escape(f.family)}"]`).forEach((n) => n.remove());
    const css = f.files
      .filter((file: any) => file?.url)
      .map((file: any) =>
        `@font-face{font-family:"${f.family}";font-style:${file.style === "italic" ? "italic" : "normal"};font-weight:${file.weight || 400};font-display:swap;src:url("${file.url}")}`
      )
      .join("\n");
    if (!css) continue;
    const style = document.createElement("style");
    style.dataset.customFont = f.family;
    style.textContent = css;
    document.head.appendChild(style);
  }
}

function applyTheme(t: ThemeSettings) {
  const root = document.documentElement;

  // Custom fonts MUST be registered before any `font-family` CSS variable
  // is set, otherwise the browser briefly renders the storefront in the
  // fallback font until the @font-face rule lands.
  if (t.customFonts) registerCustomFonts(t.customFonts);

  // Custom (uploaded) fonts shouldn't go through Google Fonts at all.
  const customFamilies = new Set(
    Array.isArray(t.customFonts) ? t.customFonts.map((f: any) => f?.family).filter(Boolean) : []
  );
  const tryLoadGoogle = (name?: string) => {
    if (!name || customFamilies.has(name)) return;
    loadGoogleFont(name);
  };
  tryLoadGoogle(t.fontFamily);
  tryLoadGoogle(t.fontFamilyAr);
  tryLoadGoogle(t.headingFont);
  tryLoadGoogle(t.bodyFont);

  if (t.fontFamilyAr) root.style.setProperty("--theme-font-ar", t.fontFamilyAr);
  if (t.fontFamily) root.style.setProperty("--theme-font-en", t.fontFamily);

  const dir = document.documentElement.dir || "rtl";
  let appFontSans: string;
  if (dir === "rtl") {
    const arFont = t.fontFamilyAr || t.fontFamily || "Tajawal";
    const enFallback = t.fontFamily || "Inter";
    appFontSans = `"${arFont}", "${enFallback}", sans-serif`;
  } else {
    const enFont = t.fontFamily || "Inter";
    const arFallback = t.fontFamilyAr || "";
    appFontSans = arFallback ? `"${enFont}", "${arFallback}", sans-serif` : `"${enFont}", sans-serif`;
  }
  root.style.setProperty("--app-font-sans", appFontSans);

  // index.css applies var(--theme-body-font) and var(--theme-heading-font) to
  // body / headings. If we don't set them, the appearance picker has no
  // visible effect on the storefront. Prefer the explicit advanced values
  // (bodyFont/headingFont) and fall back to the basic font family stack.
  const bodyStack = t.bodyFont ? `"${t.bodyFont}", ${appFontSans}` : appFontSans;
  const headingStack = t.headingFont ? `"${t.headingFont}", ${appFontSans}` : bodyStack;
  root.style.setProperty("--theme-body-font", bodyStack);
  root.style.setProperty("--theme-heading-font", headingStack);

  document.body.style.fontFamily = "";

  if (t.primaryColor) {
    const hsl = hexToHsl(t.primaryColor);
    if (hsl) root.style.setProperty("--primary", hsl);
    root.style.setProperty("--theme-primary", t.primaryColor);
  }
  if (t.secondaryColor) root.style.setProperty("--theme-secondary", t.secondaryColor);
  if (t.accentColor) {
    const hsl = hexToHsl(t.accentColor);
    if (hsl) root.style.setProperty("--accent", hsl);
    root.style.setProperty("--theme-accent", t.accentColor);
    // The accent color drives the Add-to-Cart buttons (product cards,
    // quick view, product page, bundles). Text color is auto-contrast so
    // a light accent gets dark text and vice-versa.
    root.style.setProperty("--theme-cart-btn-bg", t.accentColor);
    root.style.setProperty("--theme-cart-btn-text", contrastColor(t.accentColor));
  }
  if (t.backgroundColor) {
    root.style.setProperty("--theme-bg", t.backgroundColor);
    const bgHsl = hexToHsl(t.backgroundColor);
    if (bgHsl) root.style.setProperty("--background", bgHsl);
    const auto = contrastColor(t.backgroundColor);
    const autoHsl = hexToHsl(auto);
    if (autoHsl) root.style.setProperty("--foreground", autoHsl);
    root.style.setProperty("--theme-text", auto);
  } else if (t.textColor) {
    root.style.setProperty("--theme-text", t.textColor);
  }

  if (t.sectionHeadingColor) root.style.setProperty("--theme-heading-color-light", t.sectionHeadingColor);
  if (t.sectionHeadingColorDark) root.style.setProperty("--theme-heading-color-dark", t.sectionHeadingColorDark);

  if (t.headerBg) root.style.setProperty("--theme-header-bg", t.headerBg);
  if (t.headerTextColor) root.style.setProperty("--theme-header-text", t.headerTextColor);
  if (t.footerBg) root.style.setProperty("--theme-footer-bg", t.footerBg);
  if (t.footerTextColor) root.style.setProperty("--theme-footer-text", t.footerTextColor);
  if (t.topBarBg) root.style.setProperty("--theme-topbar-bg", t.topBarBg);
  if (t.topBarTextColor) root.style.setProperty("--theme-topbar-text", t.topBarTextColor);
  if (t.borderRadius) root.style.setProperty("--radius", t.borderRadius);
  if (t.bannerRadius) root.style.setProperty("--theme-banner-radius", t.bannerRadius);
  // Unified corner-radius variables. Each one falls back to the global
  // --radius via the CSS-side `var(name, var(--radius, …))` chain that
  // consumers (Card/Tabs/Button) declare, so a merchant who only sets
  // the global value gets it applied everywhere without per-category
  // tweaks. Setting a category value overrides just that surface.
  if (t.cardRadius) root.style.setProperty("--theme-card-radius", t.cardRadius);
  if (t.tabsRadius) root.style.setProperty("--theme-tabs-radius", t.tabsRadius);
  if (t.productImageRadius) root.style.setProperty("--theme-product-image-radius", t.productImageRadius);

  if (t.primaryBtnBg) root.style.setProperty("--theme-btn-primary-bg", t.primaryBtnBg);
  if (t.primaryBtnText) root.style.setProperty("--theme-btn-primary-text", t.primaryBtnText);
  if (t.primaryBtnRadius) root.style.setProperty("--theme-btn-primary-radius", t.primaryBtnRadius);
  if (t.secondaryBtnBg) root.style.setProperty("--theme-btn-secondary-bg", t.secondaryBtnBg);
  if (t.secondaryBtnText) root.style.setProperty("--theme-btn-secondary-text", t.secondaryBtnText);
  if (t.linkColor) root.style.setProperty("--theme-link-color", t.linkColor);
  if (t.linkHoverColor) root.style.setProperty("--theme-link-hover", t.linkHoverColor);
  if (t.brandNameColor) root.style.setProperty("--theme-brand-name", t.brandNameColor);
  if (t.brandNameHoverColor) root.style.setProperty("--theme-brand-name-hover", t.brandNameHoverColor);

  if (t.saleBadgeBg) root.style.setProperty("--theme-sale-badge-bg", t.saleBadgeBg);
  if (t.saleBadgeText) root.style.setProperty("--theme-sale-badge-text", t.saleBadgeText);
  if (t.newBadgeBg) root.style.setProperty("--theme-new-badge-bg", t.newBadgeBg);
  if (t.newBadgeText) root.style.setProperty("--theme-new-badge-text", t.newBadgeText);
  if (t.outOfStockBadgeBg) root.style.setProperty("--theme-oos-badge-bg", t.outOfStockBadgeBg);
  if (t.outOfStockBadgeText) root.style.setProperty("--theme-oos-badge-text", t.outOfStockBadgeText);

  if (t.menuBg) root.style.setProperty("--theme-menu-bg", t.menuBg);
  if (t.menuText) root.style.setProperty("--theme-menu-text", t.menuText);
  if (t.menuHover) root.style.setProperty("--theme-menu-hover", t.menuHover);
  if (t.menuFontSize) root.style.setProperty("--theme-menu-font-size", t.menuFontSize);

  if (t.headingFont) root.style.setProperty("--theme-heading-font", `"${t.headingFont}", sans-serif`);
  if (t.bodyFont) root.style.setProperty("--theme-body-font", `"${t.bodyFont}", sans-serif`);
  if (t.headingFontWeight) root.style.setProperty("--theme-heading-weight", t.headingFontWeight);
  if (t.letterSpacing) root.style.setProperty("--theme-letter-spacing", t.letterSpacing);

  if (t.inputBorderColor) root.style.setProperty("--theme-input-border", t.inputBorderColor);
  if (t.inputFocusColor) root.style.setProperty("--theme-input-focus", t.inputFocusColor);
  if (t.inputBorderRadius) root.style.setProperty("--theme-input-radius", t.inputBorderRadius);
  if (t.inputSize === "sm" || t.inputSize === "md" || t.inputSize === "lg") {
    root.setAttribute("data-input-size", t.inputSize);
  } else {
    root.removeAttribute("data-input-size");
  }

  const animType = t.animationType;
  if (animType && (animType === "fade" || animType === "slide-up" || animType === "zoom" || animType === "none")) {
    root.setAttribute("data-anim", animType);
  } else {
    root.removeAttribute("data-anim");
  }
  if (t.scrollAnimations === "true") {
    root.setAttribute("data-scroll-anim", "1");
  } else {
    root.removeAttribute("data-scroll-anim");
  }

  if (t.footerLinkColor) root.style.setProperty("--theme-footer-link", t.footerLinkColor);
  if (t.footerLinkHoverColor) root.style.setProperty("--theme-footer-link-hover", t.footerLinkHoverColor);

  if (t.bodyBgColor) document.body.style.backgroundColor = t.bodyBgColor;
  if (t.bodyBgImage) document.body.style.backgroundImage = `url(${t.bodyBgImage})`;
  if (t.fontSize) root.style.setProperty("--theme-font-size", t.fontSize);

  const toPx = (v: string | number) => {
    const s = String(v).replace(/px$/i, "");
    const n = parseFloat(s);
    return isNaN(n) ? null : n + "px";
  };

  if (t.headerHeight) {
    const v = toPx(t.headerHeight);
    if (v) root.style.setProperty("--theme-header-height", v);
  }

  if (t.darkModeBg) {
    root.style.setProperty("--theme-dark-bg", t.darkModeBg);
    const bgHsl = hexToHsl(t.darkModeBg);
    if (bgHsl) {
      root.style.setProperty("--dark-bg-hsl", bgHsl);
      const [h, s, l] = bgHsl.split(" ");
      const lightness = parseFloat(l);
      const isLightBg = lightness >= 50;
      const cardL = isLightBg ? Math.max(0, lightness - 5) : Math.min(98, lightness + 5);
      const borderL = isLightBg ? Math.max(0, lightness - 12) : Math.min(98, lightness + 12);
      root.style.setProperty("--dark-card-hsl", `${h} ${s} ${cardL}%`);
      root.style.setProperty("--dark-border-hsl", `${h} ${s} ${borderL}%`);
    }
    const autoText = contrastColor(t.darkModeBg);
    const autoTextHsl = hexToHsl(autoText);
    root.style.setProperty("--theme-dark-text", autoText);
    if (autoTextHsl) root.style.setProperty("--dark-text-hsl", autoTextHsl);
  } else if (t.darkModeText) {
    root.style.setProperty("--theme-dark-text", t.darkModeText);
    const txtHsl = hexToHsl(t.darkModeText);
    if (txtHsl) root.style.setProperty("--dark-text-hsl", txtHsl);
  }

  if (t.breadcrumbsBg) root.style.setProperty("--theme-breadcrumbs-bg", t.breadcrumbsBg);

  if (t.pageMaxWidth) {
    const v = toPx(t.pageMaxWidth);
    if (v) root.style.setProperty("--theme-page-max-width", v);
  }

  if (t.headingFont) root.style.setProperty("--theme-heading-font", t.headingFont);
  if (t.bodyFont) root.style.setProperty("--theme-body-font", t.bodyFont);

  if (t.pageLayout === "boxed") {
    document.body.style.maxWidth = t.pageMaxWidth ? `${String(t.pageMaxWidth).replace(/px$/i, "")}px` : "1400px";
    document.body.style.margin = "0 auto";
    document.body.style.boxShadow = "0 0 30px rgba(0,0,0,0.08)";
  } else {
    document.body.style.maxWidth = "";
    document.body.style.margin = "";
    document.body.style.boxShadow = "";
  }

  // Storefront custom code (CSS / JS / header / footer) must NEVER apply to
  // the admin panel — operators have hit cases where a broken custom cursor
  // or stray rule made the dashboard unusable. Skip injection on /admin.
  const inAdmin =
    typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  if (t.customCss && !inAdmin) {
    let styleEl = document.getElementById("theme-custom-css") as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "theme-custom-css";
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = t.customCss;
    styleEl.disabled = false;
  } else if (inAdmin) {
    const styleEl = document.getElementById("theme-custom-css") as HTMLStyleElement | null;
    if (styleEl) styleEl.disabled = true;
  }

  if (t.customJs && !inAdmin) {
    let scriptEl = document.getElementById("theme-custom-js");
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.id = "theme-custom-js";
      document.body.appendChild(scriptEl);
    }
    scriptEl.textContent = t.customJs;
  }

  if (t.customHeaderCode && !inAdmin) {
    let el = document.getElementById("theme-header-code");
    if (!el) {
      el = document.createElement("div");
      el.id = "theme-header-code";
      document.head.appendChild(el);
    }
    el.innerHTML = t.customHeaderCode;
  }

  if (t.customFooterCode && !inAdmin) {
    let el = document.getElementById("theme-footer-code");
    if (!el) {
      el = document.createElement("div");
      el.id = "theme-footer-code";
      document.body.appendChild(el);
    }
    el.innerHTML = t.customFooterCode;
  }

  if (t.faviconUrl) {
    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      document.head.appendChild(favicon);
    }
    const stamp = t.updatedAt ? Date.parse(String(t.updatedAt)) : 0;
    favicon.href = stamp
      ? `${t.faviconUrl}${t.faviconUrl.includes("?") ? "&" : "?"}v=${stamp}`
      : t.faviconUrl;
  }

  if (t.ogImageUrl) {
    const setOgMeta = (property: string, content: string) => {
      let m = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("property", property);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setOgMeta("og:image", t.ogImageUrl);
    let tw = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement | null;
    if (!tw) {
      tw = document.createElement("meta");
      tw.setAttribute("name", "twitter:image");
      document.head.appendChild(tw);
    }
    tw.setAttribute("content", t.ogImageUrl);
  }
}
